import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button, Paper, Breadcrumbs, Grid, Card, CardContent } from '@mui/material';
import {
    Download as DownloadIcon,
    Clear as ClearIcon,
    Assessment as AssessmentIcon,
    NavigateNext as NavigateNextIcon,
    Storage as StorageIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    Link as LinkIcon,
    Speed as SpeedIcon,
    CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useNotification } from '../../../components/Notification';
import Table from '../../../components/Table';
import Input from '../../../components/Input';
import MultiSelect from '../../../components/MultiSelect';
import DateRangePicker from '../../../components/DateRangePicker';
import {
    fetchInit,
    fetchDatabaseAnalyses,
    selectDatabaseAnalyses,
    selectDatabaseTypes,
    selectDatabaseStatuses,
    selectUnits,
    selectDatabaseAnalysisLoading,
    selectDatabaseAnalysisError,
    selectDatabaseAnalysisSuccessMessage,
    selectDatabaseAnalysisTotal,
    selectDatabaseAnalysisPage,
    selectDatabaseAnalysisPageSize,
    selectDatabaseAnalysisSortColumn,
    selectDatabaseAnalysisSortDirection,
    selectFilterName,
    selectFilterCode,
    selectFilterUnitId,
    selectFilterDatabaseType,
    selectFilterStatus,
    selectFilterLastSyncFrom,
    selectFilterLastSyncTo,
    setPage,
    setPageSize,
    setSortColumn,
    setSortDirection,
    setFilterName,
    setFilterCode,
    setFilterUnitId,
    setFilterDatabaseType,
    setFilterStatus,
    setFilterLastSyncFrom,
    setFilterLastSyncTo,
    clearError,
    clearSuccessMessage,
    exportDatabaseAnalysesToExcel,
    exportDatabaseAnalysesToPdf,
} from './slice';

function DatabaseAnalysis() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { showNotification } = useNotification();

    const databaseAnalyses = useSelector(selectDatabaseAnalyses);
    const databaseTypes = useSelector(selectDatabaseTypes);
    const databaseStatuses = useSelector(selectDatabaseStatuses);
    const units = useSelector(selectUnits);
    const loading = useSelector(selectDatabaseAnalysisLoading);
    const error = useSelector(selectDatabaseAnalysisError);
    const successMessage = useSelector(selectDatabaseAnalysisSuccessMessage);
    const total = useSelector(selectDatabaseAnalysisTotal);
    const page = useSelector(selectDatabaseAnalysisPage);
    const pageSize = useSelector(selectDatabaseAnalysisPageSize);
    const sortColumn = useSelector(selectDatabaseAnalysisSortColumn);
    const sortDirection = useSelector(selectDatabaseAnalysisSortDirection);
    const filterName = useSelector(selectFilterName);
    const filterCode = useSelector(selectFilterCode);
    const filterUnitId = useSelector(selectFilterUnitId);
    const filterDatabaseType = useSelector(selectFilterDatabaseType);
    const filterStatus = useSelector(selectFilterStatus);
    const filterLastSyncFrom = useSelector(selectFilterLastSyncFrom);
    const filterLastSyncTo = useSelector(selectFilterLastSyncTo);

    // Fetch init data on mount
    useEffect(() => {
        dispatch(fetchInit());
    }, [dispatch]);

    // Normalize MultiSelect values: when one selected, send primitive; otherwise omit
    const normalizeFilter = (filterValue) => {
        const values = Array.isArray(filterValue) ? filterValue : filterValue !== null && filterValue !== undefined ? [filterValue] : [];
        const unique = [...new Set(values)];
        return unique.length === 1 ? unique[0] : undefined;
    };

    // Fetch database analyses when filters or pagination change
    useEffect(() => {
        const params = {
            page,
            pageSize,
            sortColumn,
            sortDirection,
            name: filterName || undefined,
            code: filterCode || undefined,
            unitId: normalizeFilter(filterUnitId),
            databaseType: normalizeFilter(filterDatabaseType),
            status: normalizeFilter(filterStatus),
            lastSyncFrom: filterLastSyncFrom || undefined,
            lastSyncTo: filterLastSyncTo || undefined,
        };
        dispatch(fetchDatabaseAnalyses(params));
    }, [
        dispatch,
        page,
        pageSize,
        sortColumn,
        sortDirection,
        filterName,
        filterCode,
        filterUnitId,
        filterDatabaseType,
        filterStatus,
        filterLastSyncFrom,
        filterLastSyncTo,
    ]);

    // Show error notification
    useEffect(() => {
        if (error) {
            showNotification(error, 'error');
            dispatch(clearError());
        }
    }, [error, dispatch, showNotification]);

    // Show success notification
    useEffect(() => {
        if (successMessage) {
            showNotification(successMessage, 'success');
            dispatch(clearSuccessMessage());
        }
    }, [successMessage, dispatch, showNotification]);

    const handleClearFilters = () => {
        dispatch(setFilterName(''));
        dispatch(setFilterCode(''));
        dispatch(setFilterUnitId([]));
        dispatch(setFilterDatabaseType([]));
        dispatch(setFilterStatus([]));
        dispatch(setFilterLastSyncFrom(null));
        dispatch(setFilterLastSyncTo(null));
        dispatch(setPage(1));
    };

    const handlePageChange = (newPage) => {
        dispatch(setPage(newPage));
    };

    const handlePageSizeChange = (newPageSize) => {
        dispatch(setPageSize(newPageSize));
    };

    const handleSortChange = (column, direction) => {
        dispatch(setSortColumn(column));
        dispatch(setSortDirection(direction));
    };

    const handleExportExcel = () => {
        dispatch(exportDatabaseAnalysesToExcel());
    };

    const handleExportPdf = () => {
        dispatch(exportDatabaseAnalysesToPdf());
    };

    // Calculate summary statistics
    const calculateSummary = () => {
        let totalUsedGB = 0;
        let totalCapacityGB = 0;
        let totalConnections = 0;
        let totalQueries = 0;
        let totalSuccessfulQueries = 0;
        let totalResponseTime = 0;
        let count = 0;

        databaseAnalyses.forEach((db) => {
            totalUsedGB += db.usedSizeGB || 0;
            totalCapacityGB += db.totalCapacityGB || 0;
            totalConnections += db.activeConnections || 0;
            totalQueries += db.totalQueries || 0;
            totalSuccessfulQueries += db.successfulQueries || 0;
            if (db.averageResponseTime) {
                totalResponseTime += db.averageResponseTime;
                count++;
            }
        });

        const usagePercentage = totalCapacityGB > 0 ? ((totalUsedGB / totalCapacityGB) * 100).toFixed(1) : 0;
        const successRate = totalQueries > 0 ? ((totalSuccessfulQueries / totalQueries) * 100).toFixed(1) : 0;
        const avgResponseTime = count > 0 ? (totalResponseTime / count / 1000).toFixed(2) : 0;

        return {
            totalUsedGB: totalUsedGB.toFixed(2),
            totalAvailableGB: (totalCapacityGB - totalUsedGB).toFixed(2),
            totalCapacityGB: totalCapacityGB.toFixed(2),
            totalConnections,
            usagePercentage,
            successRate,
            avgResponseTime,
        };
    };

    const summary = calculateSummary();

    // Prepare dropdown options
    const databaseTypeOptions = [{ id: '', name: t('common.all') }, ...databaseTypes.map((type) => ({ id: type.name, name: type.name }))];

    const statusOptions = [{ id: '', name: t('common.all') }, ...databaseStatuses.map((status) => ({ id: status.name, name: status.name }))];

    const unitOptions = [{ id: '', name: t('common.all') }, ...units.map((unit) => ({ id: unit.id, name: unit.name }))];

    // Table columns
    const columns = [
        { key: 'code', label: t('databaseAnalysis.table.code') },
        { key: 'name', label: t('databaseAnalysis.table.name') },
        { key: 'unitName', label: t('databaseAnalysis.table.unitName') },
        { key: 'databaseType', label: t('databaseAnalysis.table.databaseType') },
        {
            key: 'usedSizeGB',
            label: t('databaseAnalysis.table.usedSizeGB'),
            render: (value) => (value ? `${value.toFixed(2)} GB` : '-'),
        },
        {
            key: 'totalCapacityGB',
            label: t('databaseAnalysis.table.totalCapacityGB'),
            render: (value) => (value ? `${value.toFixed(2)} GB` : '-'),
        },
        {
            key: 'usagePercentage',
            label: t('databaseAnalysis.table.usagePercentage'),
            render: (value, row) => {
                if (row.usedSizeGB && row.totalCapacityGB && row.totalCapacityGB > 0) {
                    const percentage = ((row.usedSizeGB / row.totalCapacityGB) * 100).toFixed(1);
                    return `${percentage}%`;
                }
                return '-';
            },
        },
        { key: 'tableCount', label: t('databaseAnalysis.table.tableCount') },
        { key: 'activeConnections', label: t('databaseAnalysis.table.activeConnections') },
        { key: 'status', label: t('databaseAnalysis.table.status') },
        {
            key: 'totalQueries',
            label: t('databaseAnalysis.table.totalQueries'),
            render: (value) => (value ? value.toLocaleString() : '0'),
        },
        {
            key: 'successfulQueries',
            label: t('databaseAnalysis.table.successfulQueries'),
            render: (value) => (value ? value.toLocaleString() : '0'),
        },
        {
            key: 'failedQueries',
            label: t('databaseAnalysis.table.failedQueries'),
            render: (value) => (value ? value.toLocaleString() : '0'),
        },
        {
            key: 'successRate',
            label: t('databaseAnalysis.table.successRate'),
            render: (value, row) => {
                if (row.totalQueries > 0 && row.successfulQueries !== undefined) {
                    const rate = ((row.successfulQueries / row.totalQueries) * 100).toFixed(1);
                    return `${rate}%`;
                }
                return '-';
            },
        },
        {
            key: 'averageResponseTime',
            label: t('databaseAnalysis.table.averageResponseTime'),
            render: (value) => (value ? `${(value / 1000).toFixed(2)}s` : '-'),
        },
        {
            key: 'queriesPerMinute',
            label: t('databaseAnalysis.table.queriesPerMinute'),
            render: (value) => (value ? value.toLocaleString() : '-'),
        },
        {
            key: 'lastQueryTime',
            label: t('databaseAnalysis.table.lastQueryTime'),
            render: (value) => (value ? new Date(value).toLocaleString() : '-'),
        },
        {
            key: 'lastSyncTime',
            label: t('databaseAnalysis.table.lastSyncTime'),
            render: (value) => (value ? new Date(value).toLocaleString() : '-'),
        },
    ];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', overflow: 'auto' }}>
            {/* Header Section */}
            <Box sx={{ px: 3, pt: 3, pb: 2, backgroundColor: '#ffffff' }}>
                {/* Breadcrumbs */}
                <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AssessmentIcon fontSize="small" />
                        <Typography variant="body2">{t('sidebar.module6.title')}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.primary">
                        {t('sidebar.module6.databaseAnalysis')}
                    </Typography>
                </Breadcrumbs>

                {/* Page Title and Action Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h5" component="h1" fontWeight="bold" sx={{ textAlign: 'left' }}>
                        {t('databaseAnalysis.title')}
                    </Typography>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            variant="outlined"
                            startIcon={<ClearIcon />}
                            onClick={handleClearFilters}
                            id="clearFilters"
                            name="clearFilters"
                            data-testid="databaseAnalysis-button-clearFilters"
                        >
                            {t('databaseAnalysis.clearFilters')}
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<DownloadIcon />}
                            onClick={handleExportExcel}
                            id="exportExcel"
                            name="exportExcel"
                            data-testid="databaseAnalysis-button-exportExcel"
                        >
                            {t('databaseAnalysis.exportExcel')}
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<DownloadIcon />}
                            onClick={handleExportPdf}
                            id="exportPdf"
                            name="exportPdf"
                            data-testid="databaseAnalysis-button-exportPdf"
                        >
                            {t('databaseAnalysis.exportPdf')}
                        </Button>
                    </Box>
                </Box>
            </Box>

            {/* Summary Statistics Cards */}
            <Box sx={{ m: 2 }}>
                {/* First Row - Storage Metrics (3 cards) */}
                <Grid container spacing={2} sx={{ justifyContent: 'space-between', mb: 2 }}>
                    {/* Used Storage */}
                    <Grid item xs={12} sm={6} md={4} sx={{ flex: 1 }}>
                        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <CardContent sx={{ flex: 1, p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '100%' }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 1, lineHeight: 1.2 }}
                                        >
                                            {t('databaseAnalysis.summary.usedStorage')}
                                        </Typography>
                                        <Typography variant="h4" component="div" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                            {summary.totalUsedGB} GB
                                        </Typography>
                                    </Box>
                                    <StorageIcon sx={{ fontSize: 48, color: 'primary.main', opacity: 0.6, ml: 2, flexShrink: 0 }} />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Available Storage */}
                    <Grid item xs={12} sm={6} md={4} sx={{ flex: 1 }}>
                        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <CardContent sx={{ flex: 1, p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '100%' }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 1, lineHeight: 1.2 }}
                                        >
                                            {t('databaseAnalysis.summary.availableStorage')}
                                        </Typography>
                                        <Typography variant="h4" component="div" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                            {summary.totalAvailableGB} GB
                                        </Typography>
                                    </Box>
                                    <StorageIcon sx={{ fontSize: 48, color: 'success.main', opacity: 0.6, ml: 2, flexShrink: 0 }} />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Total Capacity */}
                    <Grid item xs={12} sm={6} md={4} sx={{ flex: 1 }}>
                        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <CardContent sx={{ flex: 1, p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '100%' }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 1, lineHeight: 1.2 }}
                                        >
                                            {t('databaseAnalysis.summary.totalCapacity')}
                                        </Typography>
                                        <Typography variant="h4" component="div" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                            {summary.totalCapacityGB} GB
                                        </Typography>
                                    </Box>
                                    <StorageIcon sx={{ fontSize: 48, color: 'info.main', opacity: 0.6, ml: 2, flexShrink: 0 }} />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Second Row - Performance Metrics (4 cards) */}
                <Grid container spacing={2} sx={{ justifyContent: 'space-between' }}>
                    {/* Active Connections */}
                    <Grid item xs={12} sm={6} md={3} sx={{ flex: 1 }}>
                        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <CardContent sx={{ flex: 1, p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '100%' }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 1, lineHeight: 1.2 }}
                                        >
                                            {t('databaseAnalysis.summary.activeConnections')}
                                        </Typography>
                                        <Typography variant="h4" component="div" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                            {summary.totalConnections}
                                        </Typography>
                                    </Box>
                                    <LinkIcon sx={{ fontSize: 48, color: 'warning.main', opacity: 0.6, ml: 2, flexShrink: 0 }} />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Average Response Time */}
                    <Grid item xs={12} sm={6} md={3} sx={{ flex: 1 }}>
                        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <CardContent sx={{ flex: 1, p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '100%' }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 1, lineHeight: 1.2 }}
                                        >
                                            {t('databaseAnalysis.performanceMetrics.avgResponseTime')}
                                        </Typography>
                                        <Typography variant="h4" component="div" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                            {summary.avgResponseTime}s
                                        </Typography>
                                    </Box>
                                    <SpeedIcon sx={{ fontSize: 48, color: 'error.main', opacity: 0.6, ml: 2, flexShrink: 0 }} />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Success Rate */}
                    <Grid item xs={12} sm={6} md={3} sx={{ flex: 1 }}>
                        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <CardContent sx={{ flex: 1, p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '100%' }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 1, lineHeight: 1.2 }}
                                        >
                                            {t('databaseAnalysis.performanceMetrics.successRate')}
                                        </Typography>
                                        <Typography variant="h4" component="div" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                            {summary.successRate}%
                                        </Typography>
                                    </Box>
                                    <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', opacity: 0.6, ml: 2, flexShrink: 0 }} />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Usage Percentage */}
                    <Grid item xs={12} sm={6} md={3} sx={{ flex: 1 }}>
                        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <CardContent sx={{ flex: 1, p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '100%' }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 1, lineHeight: 1.2 }}
                                        >
                                            {t('databaseAnalysis.performanceMetrics.usagePercentage')}
                                        </Typography>
                                        <Typography variant="h4" component="div" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                            {summary.usagePercentage}%
                                        </Typography>
                                    </Box>
                                    <AssessmentIcon sx={{ fontSize: 48, color: 'info.main', opacity: 0.6, ml: 2, flexShrink: 0 }} />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* Main Content */}
            <Paper
                elevation={0}
                sx={{ borderRadius: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#ffffff' }}
            >
                {/* Filter Section */}
                <Box sx={{ px: 3, pt: 2, pb: 2, borderBottom: '1px solid #e5e7eb' }}>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(6, minmax(180px, 1fr))',
                            gap: 2,
                        }}
                    >
                        <Input
                            label={t('databaseAnalysis.filters.name')}
                            value={filterName}
                            onChange={(e) => dispatch(setFilterName(e.target.value))}
                            id="filterName"
                            name="filterName"
                            data-testid="databaseAnalysis-input-filterName"
                            fullWidth
                        />
                        <Input
                            label={t('databaseAnalysis.filters.code')}
                            value={filterCode}
                            onChange={(e) => dispatch(setFilterCode(e.target.value))}
                            id="filterCode"
                            name="filterCode"
                            data-testid="databaseAnalysis-input-filterCode"
                            fullWidth
                        />
                        <MultiSelect
                            label={t('databaseAnalysis.filters.unit')}
                            value={filterUnitId}
                            onChange={(e) => dispatch(setFilterUnitId(e.target.value))}
                            options={unitOptions}
                            id="filterUnitId"
                            name="filterUnitId"
                            data-testid="databaseAnalysis-select-filterUnitId"
                            fullWidth
                        />
                        <MultiSelect
                            label={t('databaseAnalysis.filters.databaseType')}
                            value={filterDatabaseType}
                            onChange={(e) => dispatch(setFilterDatabaseType(e.target.value))}
                            options={databaseTypeOptions}
                            id="filterDatabaseType"
                            name="filterDatabaseType"
                            data-testid="databaseAnalysis-select-filterDatabaseType"
                            fullWidth
                        />
                        <MultiSelect
                            label={t('databaseAnalysis.filters.status')}
                            value={filterStatus}
                            onChange={(e) => dispatch(setFilterStatus(e.target.value))}
                            options={statusOptions}
                            id="filterStatus"
                            name="filterStatus"
                            data-testid="databaseAnalysis-select-filterStatus"
                            fullWidth
                        />
                        <DateRangePicker
                            label={t('databaseAnalysis.filters.timeRange')}
                            fromValue={filterLastSyncFrom}
                            toValue={filterLastSyncTo}
                            onFromChange={(e) => dispatch(setFilterLastSyncFrom(e.target.value || null))}
                            onToChange={(e) => dispatch(setFilterLastSyncTo(e.target.value || null))}
                            id="filterTimeRange"
                            name="filterTimeRange"
                            data-testid="databaseAnalysis-daterangepicker-filterTimeRange"
                        />
                    </Box>
                </Box>

                {/* Table */}
                <Box sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: 3, backgroundColor: '#ffffff' }}>
                    <Table
                        id="databaseAnalysis-table"
                        columns={columns}
                        data={databaseAnalyses}
                        loading={loading}
                        emptyMessage={t('table.noData')}
                        idField="id"
                        pagination={true}
                        page={page}
                        pageSize={pageSize}
                        totalItems={total}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        pageSizeOptions={[5, 10, 20, 50, 100]}
                        sortable={true}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSortChange={handleSortChange}
                        sortableColumns={[
                            'code',
                            'name',
                            'databaseType',
                            'usedSizeGB',
                            'totalCapacityGB',
                            'tableCount',
                            'activeConnections',
                            'status',
                            'totalQueries',
                            'successfulQueries',
                            'failedQueries',
                            'averageResponseTime',
                            'queriesPerMinute',
                            'lastQueryTime',
                            'lastSyncTime',
                        ]}
                    />
                </Box>
            </Paper>
        </Box>
    );
}

export default DatabaseAnalysis;
